/**
 * The player card: one player in full, opened from the Players list.
 *
 * Read side: since when they play ("IN THE GAME SINCE" + how long that is), last
 * online or "Online now" (socket presence), signed-in sessions, balances, counts
 * (invoices, purchases, withdrawals), KYC, ban state, whether an operator created
 * the account (its invoices are never e-mailed), and the last 15 money movements.
 *
 * Edit side, each its own server action and audit line:
 *  - PROFILE & BILLING ADDRESS — one form; SAVE sends only the fields that changed,
 *    RESET puts the form back to what the server holds.
 *  - Level and hero — their own small form.
 *  - Switches (block deposits, block withdrawals, admin-created) — saved on toggle.
 *  - Ban / KYC / Add Shrooms — destructive or money actions, two clicks each.
 *
 * Below both columns, the ACTIVITY LOG (activity-log.ts): every event on the player's log on
 * MAD's six category chips, with search and paging, read-only.
 *
 * Fields and behaviour follow Dominion's card; the look is this panel's own (ui.ts).
 */
import { el, clear, toast } from '../../dom';
import { adminApi, type AdminActivityRow, type AdminPlayerCard, type AdminPlayerUpdate } from '../../game-api';
import { HERO_PRESETS } from '../../character/presets';
import { activityLog } from './activity-log';
import {
  activityLabel, fmtActivityAmount, fmtDateTime, fmtNum, isEmail, localDateTimeValue, memberFor,
  SHROOM_REASON_MAX, validateShroomGrant,
} from './format';
import {
  apiMessage, btn, checkbox, confirmBtn, errorBox, fieldError, input, labelled, LABEL,
  modal, pill, setFieldError, spinner, toastError,
} from './ui';

export interface PlayerCardHooks {
  /** Something about the player changed — the list should reload. */
  onChanged: () => void;
  onInvoice: (p: AdminPlayerCard) => void;
}

const SELECT = 'w-full px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text outline-none focus:border-gold';

/** The registration rule (core/auth/username.ts) — the server applies the same one. */
export const NICKNAME_RE = /^[a-zA-Z0-9_-]+(?: [a-zA-Z0-9_-]+)?$/;
export const NICKNAME_MIN = 3;
export const NICKNAME_MAX = 32;

/** Profile & billing text fields, their limits and labels — the server's limits. */
export const PROFILE_TEXT = {
  firstName: { max: 60, label: 'First name' },
  lastName: { max: 60, label: 'Last name' },
  address: { max: 200, label: 'Address' },
  city: { max: 100, label: 'City' },
  zipCode: { max: 20, label: 'ZIP' },
  country: { max: 2, label: 'Country (ISO-2)' },
  stateCode: { max: 3, label: 'State' },
  phone: { max: 30, label: 'Phone' },
} as const;
export type ProfileTextField = keyof typeof PROFILE_TEXT;

/** Countries whose addresses carry a state/province code. */
export const STATE_COUNTRIES: readonly string[] = ['US', 'CA', 'AU'];
export function needsState(country: string): boolean {
  return STATE_COUNTRIES.includes(country.trim().toUpperCase());
}

export type ProfileForm = { nickname: string; email: string; lastSeen: string } & Record<ProfileTextField, string>;

/** The form exactly as the server holds the player — what RESET restores. */
export function profileForm(card: AdminPlayerCard): ProfileForm {
  return {
    nickname: card.nickname ?? '',
    email: card.email,
    lastSeen: card.lastSeenAt ? localDateTimeValue(new Date(card.lastSeenAt)) : '',
    firstName: card.firstName ?? '',
    lastName: card.lastName ?? '',
    address: card.address ?? '',
    city: card.city ?? '',
    zipCode: card.zipCode ?? '',
    country: card.country ?? '',
    stateCode: card.stateCode ?? '',
    phone: card.phone ?? '',
  };
}

/** The profile fields that differ from what the server holds — all a SAVE sends. */
export function profileChanges(card: AdminPlayerCard, form: ProfileForm): AdminPlayerUpdate {
  const out: AdminPlayerUpdate = {};
  if (form.nickname.trim() !== (card.nickname ?? '')) out.nickname = form.nickname.trim();
  if (form.email.trim().toLowerCase() !== card.email) out.email = form.email.trim();
  for (const field of Object.keys(PROFILE_TEXT) as ProfileTextField[]) {
    const upper = field === 'country' || field === 'stateCode';
    const now = upper ? form[field].trim().toUpperCase() : form[field].trim();
    // "" is how a field is cleared — the server stores it as null.
    if (now !== (card[field] ?? '')) out[field] = now;
  }
  // Compared at the picker's resolution (minutes): re-saving an untouched card must
  // not shave the seconds off the stored value.
  const before = card.lastSeenAt ? Math.floor(new Date(card.lastSeenAt).getTime() / 60_000) : null;
  const seen = form.lastSeen ? new Date(form.lastSeen) : null;
  const after = seen && !Number.isNaN(seen.getTime()) ? Math.floor(seen.getTime() / 60_000) : null;
  if (after !== before) out.lastSeenAt = seen && after !== null ? seen.toISOString() : null;
  return out;
}

/** Client-side checks mirroring the server's, so the operator sees them by the field. */
export function validateProfile(form: ProfileForm): Partial<Record<keyof ProfileForm, string>> {
  const errors: Partial<Record<keyof ProfileForm, string>> = {};
  const nick = form.nickname.trim();
  if (nick.length < NICKNAME_MIN || nick.length > NICKNAME_MAX || !NICKNAME_RE.test(nick)) {
    errors.nickname = `${NICKNAME_MIN}–${NICKNAME_MAX} characters: letters, digits, _ or -, one space between words`;
  }
  const mail = form.email.trim();
  if (!isEmail(mail) || mail.length > 254) errors.email = 'Enter a valid email';
  for (const field of Object.keys(PROFILE_TEXT) as ProfileTextField[]) {
    if (form[field].trim().length > PROFILE_TEXT[field].max) errors[field] = `At most ${PROFILE_TEXT[field].max} characters`;
  }
  if (form.country.trim() && !/^[A-Za-z]{2}$/.test(form.country.trim())) errors.country = 'Two letters, e.g. LV';
  if (form.lastSeen) {
    const at = new Date(form.lastSeen);
    if (Number.isNaN(at.getTime()) || at.getFullYear() < 2000) errors.lastSeen = 'Not a valid date';
    else if (at.getTime() > Date.now() + 60_000) errors.lastSeen = 'Cannot be in the future';
  }
  return errors;
}

/** Level / hero changes (their own small form). */
export function gameChanges(card: AdminPlayerCard, form: { level: string; heroPreset: string }): AdminPlayerUpdate {
  const out: AdminPlayerUpdate = {};
  if (form.level !== '' && Number(form.level) !== card.level) out.level = Number(form.level);
  if ((form.heroPreset || null) !== card.heroPreset) out.heroPreset = form.heroPreset || null;
  return out;
}

/** "LAST ONLINE": socket presence wins over the stored timestamp. */
export function lastOnlineText(card: Pick<AdminPlayerCard, 'online' | 'lastSeenAt'>): string {
  if (card.online) return 'Online now';
  return card.lastSeenAt ? fmtDateTime(card.lastSeenAt) : 'never';
}

/** The card's switches, as the panel words them. */
export const CARD_SWITCHES = [
  { key: 'depositBlocked', label: 'BLOCK DEPOSITS', hint: 'Player cannot buy Shrooms' },
  { key: 'withdrawBlocked', label: 'BLOCK WITHDRAWALS', hint: 'Player cannot request payouts' },
  { key: 'createdByAdmin', label: 'ADMIN-CREATED ACCOUNT', hint: 'Invoices are never emailed to this player' },
] as const;

export function activityRow(a: AdminActivityRow): HTMLElement {
  const tone = a.delta > 0 ? 'text-leaf' : a.delta < 0 ? 'text-[#ffb3a7]' : 'text-text-dim';
  return el('li', 'flex items-baseline gap-3 py-1.5 border-t border-border/60 first:border-t-0 text-sm', {},
    el('span', 'shrink-0 w-[8.5rem] font-mono text-[11px] text-text-dim', {}, fmtDateTime(a.createdAt)),
    el('span', 'flex-1 min-w-0 truncate', { title: a.description }, a.description || a.type),
    el('span', `shrink-0 font-mono ${tone}`, {}, fmtActivityAmount(a.delta, a.currency)));
}

function section(title: string, ...children: (Node | null)[]): HTMLElement {
  return el('section', 'flex flex-col gap-3 rounded-lg border border-border bg-surface-2/40 p-3', {},
    el('div', 'text-[11px] uppercase tracking-widest text-[#ffd97a] font-mono', {}, title),
    ...children.filter((c): c is Node => c != null));
}

function fact(label: string, value: Node | string, hint?: string) {
  return el('div', 'min-w-0', {},
    el('div', 'text-[10px] uppercase tracking-widest text-text-dim font-mono', {}, label),
    el('div', 'text-sm truncate', {}, value),
    hint ? el('div', 'text-[11px] text-text-dim truncate', {}, hint) : null);
}

export function openPlayerCard(playerId: string, title: string, hooks: PlayerCardHooks): void {
  const body = el('div', 'min-h-[120px]', {}, spinner());
  let stopLog = () => {};
  const shut = () => { stopLog(); close(); };
  const close = modal(`Player card — ${title}`, body, [btn('Close', () => shut())], () => stopLog(), { size: 'wide' });

  async function load() {
    clear(body);
    body.appendChild(spinner());
    try {
      draw(await adminApi.playerCard(playerId));
    } catch (e) {
      clear(body);
      body.appendChild(errorBox(apiMessage(e, "Couldn't load the player."), () => void load()));
    }
  }

  /** Run one card action, then reload the card and the list. */
  async function act(run: () => Promise<unknown>, done: string, failed: string) {
    try {
      await run();
      toast(done);
      hooks.onChanged();
      await load();
    } catch (e) {
      toastError(e, failed);
    }
  }

  function draw(c: AdminPlayerCard) {
    clear(body);
    stopLog();
    const log = activityLog({ playerId: c.id, playerName: c.nickname || c.username });
    stopLog = log.stop;
    body.appendChild(el('div', 'flex flex-col gap-4', {},
      el('div', 'grid gap-4 lg:grid-cols-2 items-start', {},
        el('div', 'flex flex-col gap-4 min-w-0', {}, factsBlock(c), profileBlock(c), gameBlock(c)),
        el('div', 'flex flex-col gap-4 min-w-0', {}, controlsBlock(c), shroomsBlock(c), activityBlock(c))),
      section('ACTIVITY LOG', log.node)));
  }

  function factsBlock(c: AdminPlayerCard): HTMLElement {
    const since = c.createdAt ?? c.joinedAt;
    return el('div', 'flex flex-col gap-3', {},
      el('div', 'flex flex-wrap items-center gap-1.5', {},
        c.online ? pill('online now', 'ok') : null,
        c.createdByAdmin ? pill('admin-created · no invoice e-mails', 'warn') : null,
        c.banned ? pill('banned', 'bad') : null,
        c.depositBlocked ? pill('deposits blocked', 'bad') : null,
        c.withdrawBlocked ? pill('withdrawals blocked', 'bad') : null,
        c.isBot ? pill('bot', 'mute') : null),
      el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));' },
        fact('In the game since', fmtDateTime(since), memberFor(since)),
        fact('Last online', lastOnlineText(c)),
        fact('Activity', pill(activityLabel(c.activity).text, activityLabel(c.activity).tone)),
        fact('Active sessions', fmtNum(c.activeSessions)),
        fact('Level / XP', `${c.level} · ${fmtNum(c.xp)} XP`),
        fact('Shrooms', fmtNum(c.shrooms)),
        fact('Coins', fmtNum(c.coins)),
        fact('Email', c.emailVerified ? pill('verified', 'ok') : pill('unverified', 'warn')),
        fact('KYC', c.kycVerified ? pill('approved', 'ok') : pill(c.kycStatus.toLowerCase(), 'warn')),
        fact('Invoices', fmtNum(c.invoiceCount)),
        fact('Purchases', fmtNum(c.purchaseCount)),
        fact('Withdrawals', fmtNum(c.withdrawalCount)),
        fact('Deposited', c.wallet ? `${fmtNum(c.wallet.lifetimeDeposited)} Shrooms` : '0'),
        fact('Withdrawn', c.wallet ? `${fmtNum(c.wallet.lifetimeWithdrawn)} Shrooms` : '0')));
  }

  function profileBlock(c: AdminPlayerCard): HTMLElement {
    const original = profileForm(c);
    const nickname = input('Nickname', original.nickname);
    const email = input('player@example.com', original.email, 'email');
    const lastSeen = input('', original.lastSeen, 'datetime-local');
    const text = {} as Record<ProfileTextField, HTMLInputElement>;
    for (const f of Object.keys(PROFILE_TEXT) as ProfileTextField[]) {
      text[f] = input(f === 'country' ? 'LV' : f === 'stateCode' ? 'US/CA/AU' : f === 'address' ? 'Street, house, apartment' : '', original[f]);
      text[f].maxLength = PROFILE_TEXT[f].max;
    }
    nickname.maxLength = NICKNAME_MAX;
    email.maxLength = 254;
    const errors = Object.fromEntries(
      (['nickname', 'email', 'lastSeen', ...Object.keys(PROFILE_TEXT)] as (keyof ProfileForm)[]).map((k) => [k, fieldError()]),
    ) as Record<keyof ProfileForm, HTMLElement>;
    const read = (): ProfileForm => ({
      nickname: nickname.value, email: email.value, lastSeen: lastSeen.value,
      ...Object.fromEntries((Object.keys(PROFILE_TEXT) as ProfileTextField[]).map((f) => [f, text[f].value])) as Record<ProfileTextField, string>,
    });
    const field = (label: string, control: HTMLElement, key: keyof ProfileForm, cls = '') =>
      el('div', `min-w-0 ${cls}`, {}, labelled(label, control), errors[key]);

    const stateWrap = field(PROFILE_TEXT.stateCode.label, text.stateCode, 'stateCode');
    const syncState = () => stateWrap.classList.toggle('hidden', !needsState(text.country.value) && !text.stateCode.value);

    const save = btn('SAVE', () => void submit(), 'primary');
    const reset = btn('RESET', () => {
      nickname.value = original.nickname; email.value = original.email; lastSeen.value = original.lastSeen;
      for (const f of Object.keys(PROFILE_TEXT) as ProfileTextField[]) text[f].value = original[f];
      for (const e of Object.values(errors)) setFieldError(e);
      refresh();
    });
    const pending = el('span', 'text-[11px] text-text-dim', {});
    const refresh = () => {
      const n = Object.keys(profileChanges(c, read())).length;
      save.disabled = n === 0;
      reset.disabled = n === 0;
      pending.textContent = n ? `${n} unsaved change${n === 1 ? '' : 's'}` : 'No changes';
      syncState();
    };

    async function submit() {
      const form = read();
      const bad = validateProfile(form);
      for (const [k, node] of Object.entries(errors)) setFieldError(node, bad[k as keyof ProfileForm]);
      if (Object.keys(bad).length) return;
      const patch = profileChanges(c, form);
      if (!Object.keys(patch).length) { toast('Nothing changed'); return; }
      save.disabled = true;
      await act(() => adminApi.updatePlayer(c.id, patch), 'Player saved', 'Could not save the player');
      save.disabled = false;
    }

    const box = section('PROFILE & BILLING ADDRESS',
      el('div', 'grid gap-3 sm:grid-cols-2', {},
        field('Nickname', nickname, 'nickname'),
        field('Email', email, 'email'),
        field(PROFILE_TEXT.firstName.label, text.firstName, 'firstName'),
        field(PROFILE_TEXT.lastName.label, text.lastName, 'lastName'),
        field(PROFILE_TEXT.address.label, text.address, 'address'),
        field('Last online', lastSeen, 'lastSeen')),
      el('div', 'grid gap-3 grid-cols-2 sm:grid-cols-4', {},
        field(PROFILE_TEXT.city.label, text.city, 'city', 'col-span-2'),
        field(PROFILE_TEXT.zipCode.label, text.zipCode, 'zipCode'),
        field(PROFILE_TEXT.country.label, text.country, 'country'),
        stateWrap,
        field(PROFILE_TEXT.phone.label, text.phone, 'phone', 'col-span-2')),
      el('div', 'flex flex-wrap items-center gap-2', {}, save, reset, pending));
    box.addEventListener('input', refresh);
    refresh();
    return box;
  }

  function gameBlock(c: AdminPlayerCard): HTMLElement {
    const level = input('1', String(c.level), 'number');
    level.min = '1'; level.max = String(c.maxLevel);
    const levelErr = fieldError();
    const hero = el('select', SELECT, {}, el('option', '', { value: '' }, '— none applied —')) as HTMLSelectElement;
    for (const id of c.heroPresets) {
      const preset = HERO_PRESETS.find((h) => h.id === id);
      hero.appendChild(el('option', '', { value: id }, preset ? `${preset.label} — ${preset.blurb}` : id));
    }
    hero.value = c.heroPreset ?? '';
    const apply = btn('APPLY', async () => {
      const lvl = Number(level.value);
      setFieldError(levelErr);
      if (!Number.isInteger(lvl) || lvl < 1 || lvl > c.maxLevel) { setFieldError(levelErr, `1–${c.maxLevel}`); return; }
      const patch = gameChanges(c, { level: level.value, heroPreset: hero.value });
      if (!Object.keys(patch).length) { toast('Nothing changed'); return; }
      await act(() => adminApi.updatePlayer(c.id, patch), 'Player saved', 'Could not save the player');
    }, 'primary');
    return section('HERO & LEVEL',
      el('div', 'grid gap-3 sm:grid-cols-2', {},
        el('div', 'min-w-0', {}, labelled(`Level (1–${c.maxLevel})`, level), levelErr),
        labelled('Hero', hero)),
      el('div', 'text-[11px] text-text-dim', {},
        'Terraboma heroes are looks, not classes. Changing the level moves XP to that level\'s threshold.'),
      el('div', 'flex', {}, apply));
  }

  function controlsBlock(c: AdminPlayerCard): HTMLElement {
    const who = c.nickname ?? c.email;
    const switches = CARD_SWITCHES.map((s) => {
      const box = checkbox(s.label, c[s.key]);
      box.input.addEventListener('change', () => {
        const next = box.input.checked;
        box.input.disabled = true;
        void act(() => adminApi.updatePlayer(c.id, { [s.key]: next }),
          `${s.label.toLowerCase()} ${next ? 'on' : 'off'}`, 'Could not update the player')
          .finally(() => { box.input.disabled = false; });
      });
      return el('div', 'rounded-lg border border-border px-3 py-2', {},
        box.wrap, el('div', 'text-[11px] text-text-dim mt-0.5 pl-6', {}, s.hint));
    });

    const rejectReason = input('Reason sent to the player', 'Documents could not be verified');
    rejectReason.maxLength = 1000;
    const kycPending = c.kycStatus === 'PENDING';

    return section('ACCOUNT CONTROLS',
      el('div', 'flex flex-wrap gap-1.5', {},
        c.banned
          ? confirmBtn('UNBAN', () => act(() => adminApi.setBanned(c.id, false), `${who} unbanned`, 'Could not unban'),
            { variant: 'default', armedLabel: 'CLICK TO CONFIRM UNBAN' })
          : confirmBtn('BAN', () => act(() => adminApi.setBanned(c.id, true), `${who} banned`, 'Could not ban'),
            { armedLabel: 'CLICK TO CONFIRM BAN' }),
        c.kycVerified ? null
          : confirmBtn('CONFIRM KYC', () => act(() => adminApi.confirmKyc(c.id, true), 'KYC confirmed', 'Could not confirm KYC'),
            { variant: 'primary' }),
        kycPending
          ? confirmBtn('REJECT KYC', async () => {
            const reason = rejectReason.value.trim();
            if (!reason) { toast('Give a reason for the rejection', 'error'); return; }
            await act(() => adminApi.rejectKyc(c.id, reason), 'KYC rejected', 'Could not reject KYC');
          })
          : null,
        c.emailVerified ? null
          : btn('VERIFY EMAIL', () => void act(() => adminApi.verifyEmail(c.id), 'Email verified', 'Could not verify the email')),
        btn('Invoice', () => { shut(); hooks.onInvoice(c); })),
      kycPending ? labelled('KYC rejection reason', rejectReason) : null,
      el('div', 'grid gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3', {}, ...switches));
  }

  function shroomsBlock(c: AdminPlayerCard): HTMLElement {
    const amount = input('e.g. 500', '', 'number');
    amount.min = '1'; amount.step = '1';
    const amountErr = fieldError();
    const reason = input('Optional — shown in the player\'s history');
    reason.maxLength = SHROOM_REASON_MAX;
    const reasonErr = fieldError();
    const add = confirmBtn('ADD SHROOMS', async () => {
      const errors = validateShroomGrant(amount.value, reason.value, { positiveOnly: true });
      setFieldError(amountErr, errors.amount);
      setFieldError(reasonErr, errors.reason);
      if (Object.keys(errors).length) return;
      await act(() => adminApi.giveShrooms(c.id, Number(amount.value), reason.value.trim()),
        `${fmtNum(Number(amount.value))} Shrooms added`, 'Could not add Shrooms');
    }, { variant: 'primary' });
    return section('ADD SHROOMS',
      el('div', 'grid gap-3 sm:grid-cols-[10rem_1fr]', {},
        el('div', 'min-w-0', {}, labelled('Shrooms', amount), amountErr),
        el('div', 'min-w-0', {}, labelled(`Reason (max ${SHROOM_REASON_MAX})`, reason), reasonErr)),
      el('div', 'flex', {}, add));
  }

  function activityBlock(c: AdminPlayerCard): HTMLElement {
    return section('RECENT ACTIVITY',
      c.recentActivity.length
        ? el('ul', 'flex flex-col', {}, ...c.recentActivity.map(activityRow))
        : el('div', 'text-sm text-text-dim', {}, 'No money movements yet.'),
      el('div', LABEL, {}, `Last ${c.recentActivity.length} of the Shroom/coin ledger and payments, newest first`));
  }

  void load();
}
